import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Col, Layout, message, Popconfirm, Row, Spin, Timeline } from 'antd';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import TroubleshootingStepRowDisplay from './TroubleshootingStepRowDisplay';
import { PageLoadingPlaceholder } from '../../page_loading_placeholder/PageLoadingPlaceholder';
import SubnavBar from '../../subnav_bar/SubnavBar';
import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import TroubleshootingForm from './TroubleshootingForm';
import OWTabTitleWithSubTitle from './OWTabTitleWithSubTitle';
import TroubleshootingStepsPreviewModal from './TroubleshootingStepsPreviewModal';
import { withUserType } from '../../withUserType';
import {
	publishTroubleshootingGuide,
	revertTroubleshootingGuide,
} from '../../../thunks/troubleshooting_guides_thunks';
import { areStepsAvailable } from '../../../utils/TroubleshootingUtils';

const { Content } = Layout;

const TroubleshootingDetailStepsPage: FC<any> = ({
	troubleshootingGuide,
	fetching,
	publishGuide,
	revertGuide,
}): React.ReactElement => {
	const [viewPublishedVersion, setViewPublishedVersion] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [reverting, setReverting] = useState(false);

	const [previewVisible, setPreviewVisible] = useState(false);

	const publishedVersionAvailable = useMemo(
		() => areStepsAvailable('troubleshootingSteps', troubleshootingGuide),
		[troubleshootingGuide]
	);

	const draftAvailable = useMemo(
		() => areStepsAvailable('draftTroubleshootingSteps', troubleshootingGuide),
		[troubleshootingGuide]
	);

	const previousVersionAvailable = useMemo(
		() => areStepsAvailable('previousTroubleshootingSteps', troubleshootingGuide),
		[troubleshootingGuide]
	);

	const _showPreview = () => setPreviewVisible(true);
	const _hidePreview = () => setPreviewVisible(false);

	const onPublish = useCallback(() => {
		setPublishing(true);
		publishGuide(troubleshootingGuide)
			.then(() => message.success('Changes are published successfully!'))
			.catch(message.error)
			.finally(() => setPublishing(false));
	}, [publishGuide, troubleshootingGuide]);

	const onRevert = useCallback(() => {
		setReverting(true);
		revertGuide(troubleshootingGuide)
			.then(() => message.success('Previous version restored successfully!'))
			.catch(message.error)
			.finally(() => setReverting(false));
	}, [revertGuide, troubleshootingGuide]);

	const _showPublishedVersion = () => setViewPublishedVersion(true);
	const _hidePublishedVersion = () => setViewPublishedVersion(false);

	const stepsTitle = useMemo(() => {
		const linkStyle = 'text-blue-600 underline cursor-pointer hover:text-blue-800';
		return (
			<span className="h5regular items-center gap-2">
				{draftAvailable ? (
					viewPublishedVersion ? (
						<span>
							A draft version of this troubleshooting guide exist and is not yet published. View{' '}
							<span className={linkStyle} onClick={_hidePublishedVersion}>
								draft version
							</span>
						</span>
					) : (
						<>
							<span>Your changes are saved as a draft and are not published yet.</span>
							{publishedVersionAvailable ? (
								<span className="items-center gap-2">
									View{' '}
									<span className={linkStyle} onClick={_showPublishedVersion}>
										Published Version
									</span>
								</span>
							) : null}
						</>
					)
				) : previousVersionAvailable ? (
					<span>
						Your changes are published.{' '}
						<Popconfirm
							title={
								'Restoring these changes will apply to all users immediately. Are you sure you want to restore now?'
							}
							onConfirm={onRevert}
							okText="Yes"
						>
							<span className={linkStyle}>
								Restore previous version
								{reverting ? (
									<span className="ml-2">
										<Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
									</span>
								) : null}
							</span>
						</Popconfirm>
					</span>
				) : null}
			</span>
		);
	}, [
		draftAvailable,
		onRevert,
		previousVersionAvailable,
		publishedVersionAvailable,
		reverting,
		viewPublishedVersion,
	]);

	const previewKey = useMemo(
		() =>
			viewPublishedVersion || !draftAvailable
				? 'troubleshootingSteps'
				: 'draftTroubleshootingSteps',
		[draftAvailable, viewPublishedVersion]
	);

	return fetching ? (
		<PageLoadingPlaceholder />
	) : (
		<Content
			style={{
				backgroundColor: 'white',
			}}
		>
			<Row style={{ margin: '4px', marginTop: '-16px' }} gutter={12}>
				<Col span={24} style={{ backgroundColor: `rgb(249, 249, 249)` }}>
					<SubnavBar
						left={<OWTabTitleWithSubTitle title={stepsTitle} />}
						right={
							<div className="flex flex-row items-center gap-2 py-4">
								<Button onClick={_showPreview} icon={<EyeOutlined />}>
									Preview
								</Button>
								{draftAvailable && !viewPublishedVersion ? (
									<Popconfirm
										title={
											'Publishing these changes will apply to all users immediately. Are you sure you want to publish now?'
										}
										onConfirm={onPublish}
										okText="Yes"
									>
										<Button loading={publishing} type="primary">
											Publish Guide
										</Button>
									</Popconfirm>
								) : null}
							</div>
						}
					/>
				</Col>
				<Col span={24} className="my-4">
					{!viewPublishedVersion ? (
						<TroubleshootingForm formData={troubleshootingGuide} />
					) : (
						<Timeline>
							{nullSafeGetOrElse('troubleshootingSteps', troubleshootingGuide, []).map(
								(step, idx) => {
									const stepNo = idx + 1;
									return (
										<Timeline.Item position="right">
											<TroubleshootingStepRowDisplay step={step} stepNo={stepNo} />
										</Timeline.Item>
									);
								}
							)}
						</Timeline>
					)}
				</Col>
				{previewVisible ? (
					<TroubleshootingStepsPreviewModal
						troubleshootingSteps={nullSafeGetOrElse(previewKey, troubleshootingGuide, [])}
						onCancel={_hidePreview}
					/>
				) : null}
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	fetching: state.troubleshooting_guides.fetching,
	troubleshootingGuide: state.troubleshooting_guides.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	publishGuide: (entity) => dispatch(publishTroubleshootingGuide(ownProps.userType)(entity)),
	revertGuide: (entity) => dispatch(revertTroubleshootingGuide(ownProps.userType)(entity)),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(TroubleshootingDetailStepsPage as any)
	)
);
